import { Button, Typography } from '@/components/atoms';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import MailCheck from '@assets/images/svg/mail-check.svg';
import React, { useEffect, useRef } from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';

const FRAME = {
    maxWidth: 400,
    radius: 16,
    pad: 24,
    gap: 16,
    iconSize: 80,
    buttonHeight: 48,
} as const;

export interface ServiceRequestSuccessDialogProps {
    visible: boolean;
    onOk: () => void;
}

/**
 * Web service-request submit success (Modal overlay, same pattern as UnitReservedSuccessDialog).
 */
export function ServiceRequestSuccessDialog({
    visible,
    onOk,
}: Readonly<ServiceRequestSuccessDialogProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const viewTrackedRef = useRef(false);

    useEffect(() => {
        if (!visible) {
            viewTrackedRef.current = false;
            return;
        }
        if (viewTrackedRef.current) {
            return;
        }
        viewTrackedRef.current = true;
        trackCatalogEvent({
            screen: DT_SCREEN.SERVICE_REQUEST_SUCCESS,
            actionName: 'service_request_success_dialog_view',
            eventType: 'view',
        });
    }, [visible]);

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onOk}
        >
            <View style={styles.overlay}>
                <Pressable
                    style={styles.backdrop}
                    onPress={onOk}
                    accessibilityRole="button"
                    accessibilityLabel={t('common.ok')}
                />
                <View
                    style={[
                        styles.card,
                        {
                            backgroundColor: colors.backgroundLevel1,
                            maxWidth: FRAME.maxWidth,
                        },
                    ]}
                >
                    <MailCheck width={FRAME.iconSize} height={FRAME.iconSize} />
                    <View style={styles.textBlock}>
                        <Typography variant="title3" color="primary" style={styles.title}>
                            {t('support.successTitle')}
                        </Typography>
                        <Typography variant="body" color="tertiary" style={styles.subtitle}>
                            {t('support.successSubtitle')}
                        </Typography>
                    </View>
                    <Button
                        title={t('common.ok')}
                        variant="primary"
                        fullWidth
                        onPress={onOk}
                        style={styles.okButton}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 16,
    },
    backdrop: {
        ...StyleSheet.absoluteFillObject,
    },
    card: {
        width: '100%',
        borderRadius: FRAME.radius,
        padding: FRAME.pad,
        alignItems: 'center',
        gap: FRAME.gap,
        zIndex: 1,
    },
    textBlock: {
        width: '100%',
        alignItems: 'center',
        gap: 8,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    subtitle: {
        textAlign: 'center',
        width: '100%',
        lineHeight: 22,
    },
    okButton: {
        height: FRAME.buttonHeight,
        borderRadius: 4,
        alignSelf: 'stretch',
    },
});

export default ServiceRequestSuccessDialog;
